import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Mail, Play, X } from 'lucide-react';
import ParallaxTower from './tower/ParallaxTower';

// ─── Project Data ───────────────────────────────────────────────────────────

const PROJECTS = [
  {
    id: "aba-compliance",
    title: "ABA Clinical Compliance & Billing Portal",
    subtitle: "HIPAA-COMPLIANT CLINICAL WORKFLOW",
    description: "A secure administrative suite designed for behavioral therapy clinics. Streamlines multi-practitioner client scheduling, clinical auditing, and automated insurance billing workflows.",
    videoUrl: "https://firebasestorage.googleapis.com/v0/b/tower2-4a8df.firebasestorage.app/o/Villa-ABA-Workflow.mp4?alt=media",
    thumbnailUrl: "/assets/Villa ABA Workflow Screenshot.PNG",
    points: [
      "HIPAA-compliant document storage and secure data vaulting",
      "Therapy activity logs mapped directly to insurance billing codes",
      "Real-time schedule conflict resolution for practitioners and clients",
      "Audit-ready clinical compliance tracking logs",
      "Low-latency data synchronization across clinic terminals"
    ],
    emailSubject: "Inquiry: Custom ABA Clinical Compliance & Scheduling Systems",
    emailBody: "Hi Tower Team,\n\nI was reviewing your 'ABA Clinical Compliance & Scheduling Portal' portfolio project. We are looking to implement a custom workflow for our behavioral therapy clinic and would like to learn how your team can help us achieve custom ownership of our software.\n\nBest regards,"
  },
  {
    id: "traveler-system",
    title: "Traveler QA System for Manufacturers",
    subtitle: "HIGH-CONCURRENCY SHOP FLOOR QUALITY GATES",
    description: "A ruggedized, high-throughput web application that integrates with factory floor terminals to enforce strict manufacturing quality gates and sign-offs.",
    videoUrl: "/assets/traveler-system.mp4",
    thumbnailUrl: "/assets/traveler-system-thumb.png",
    points: [
      "Strict, sequential QA checkpoint enforcement gates",
      "Custom traveler logs tracked by part and batch identifiers",
      "Real-time employee authorization and machine safety verification",
      "Instant supervisor flagging and exception authorization codes",
      "Designed for 50+ concurrent low-spec terminal displays"
    ],
    emailSubject: "Inquiry: Custom Manufacturing Traveler and QA Systems",
    emailBody: "Hi Tower Team,\n\nI was looking at the 'Traveler QA System for Manufacturers' project in your portfolio. We run operations that require specialized shop-floor tracking and would like to discuss building a custom solution.\n\nBest regards,"
  },
  {
    id: "digital-bookshelf",
    title: "Digital Library Bookshelf & Curator",
    subtitle: "INTERACTIVE ASSET CATALOGING & PROGRESS ANALYTICS",
    description: "An intuitive, fluid media portal for managing massive catalogs of digital books and reference assets with curator review workflows.",
    videoUrl: "/assets/library-digital-bookshelf.mp4",
    thumbnailUrl: "/assets/library-bookshelf-thumb.png",
    points: [
      "Highly responsive digital bookshelf visual user interface",
      "Automatic metadata ingestion and catalog tagging APIs",
      "Reader progress analytics and custom reading path recommendations",
      "Multi-user curation workflow with reviewer approval gates",
      "Dynamic indexing supporting search across thousands of volumes"
    ],
    emailSubject: "Inquiry: Custom Digital Bookshelf and Catalog Systems",
    emailBody: "Hi Tower Team,\n\nI am interested in your 'Digital Library Bookshelf & Curator' project. We need a custom indexing or cataloging interface and would like to learn about your custom pricing and delivery timelines.\n\nBest regards,"
  }
];

// ─── Animation Variants ───────────────────────────────────────────────────────

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 15,
    transition: { duration: 0.3, ease: 'easeIn' }
  }
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PortfolioPage = ({ onBack }) => {
  const [activeVideoProject, setActiveVideoProject] = useState(null);
  const videoRef = useRef(null);

  const handleOpenVideo = (project) => {
    setActiveVideoProject(project);
  };

  const handleCloseVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setActiveVideoProject(null);
  };

  const getMailtoUrl = (project) => {
    const emailTo = "dev.group@tower-integrations.com";
    return `mailto:${emailTo}?subject=${encodeURIComponent(project.emailSubject)}&body=${encodeURIComponent(project.emailBody)}`;
  };

  return (
    <div className="relative min-h-screen bg-[#023666] text-white overflow-y-auto">
      {/* Parallax Background */}
      <ParallaxTower activeIndex={5} />

      {/* Scrim overlay for legibility */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#01223f]/70 via-[#01223f]/50 to-[#01223f]/80 pointer-events-none z-0" />

      {/* Back Button */}
      <div className="relative z-20 px-6 pt-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white/90 transition-colors duration-200 group"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to Home
        </button>
      </div>

      {/* Page Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-32 pt-12">
        {/* Header Section */}
        <div className="mb-16 text-left max-w-3xl">
          <p className="text-[11px] font-medium tracking-[0.3em] text-cyan-400/80 uppercase mb-4">
            Custom Software Assets
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold font-header tracking-tight mb-6 text-transparent bg-clip-text bg-gradient-to-br from-white via-white/90 to-white/60">
            Proven Operations Platforms.
          </h1>
          <p className="text-base text-white/60 leading-relaxed">
            We build proprietary, business-critical software architectures designed around exclusive workflows.
            Review details of our recently delivered systems below
          </p>
        </div>

        {/* Dynamic Project Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {PROJECTS.map((project) => (
            <motion.div
              key={project.id}
              variants={cardVariants}
              className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl hover:border-cyan-400/30 hover:bg-white/[0.07] transition-all duration-300 flex flex-col group card-crystal"
            >
              {/* Top highlight gradient */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent rounded-t-3xl" />

              {/* Media Card Thumbnail (Image with Hover Play Overlay) */}
              <div className="relative w-full aspect-video rounded-t-3xl overflow-hidden bg-black/40 border-b border-white/5">
                <img
                  src={project.thumbnailUrl}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />

                {/* Visual Video Play Overlay */}
                <div
                  onClick={() => handleOpenVideo(project)}
                  className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer"
                >
                  <div className="p-4 rounded-full bg-white/10 backdrop-blur-md border border-white/30 text-white scale-90 group-hover:scale-100 transition-transform duration-300 hover:bg-cyan-500/20 hover:border-cyan-400/50 hover:text-cyan-300 shadow-[0_0_20px_rgba(34,211,238,0.2)]">
                    <Play className="w-6 h-6 fill-current ml-0.5" />
                  </div>
                </div>
              </div>

              {/* Card Details Content */}
              <div className="p-6 flex flex-col flex-grow">
                {/* Category Subtitle */}
                <span className="text-[10px] font-semibold tracking-widest text-cyan-400/70 uppercase mb-2">
                  {project.subtitle}
                </span>

                {/* Card Title */}
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">
                  {project.title}
                </h3>

                {/* Description */}
                <p className="text-[13.5px] text-white/50 leading-relaxed mb-6">
                  {project.description}
                </p>

                {/* Checklist (Points) */}
                <ul className="space-y-2 mb-8 flex-grow">
                  {project.points.map((point, index) => (
                    <li key={index} className="flex items-start gap-2.5 text-[13px] text-white/70 leading-snug">
                      <div className="mt-1 shrink-0 text-cyan-400/80">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Action Buttons */}
                <div className="flex flex-col gap-3 mt-auto">
                  {/* Watch Video Button */}
                  <button
                    onClick={() => handleOpenVideo(project)}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium text-sm border border-white/10 hover:border-white/25 bg-white/[0.02] hover:bg-white/[0.08] text-white transition-all duration-200"
                  >
                    <Play className="w-4 h-4 fill-current ml-0.5 text-cyan-400" />
                    Watch Video Showcase
                  </button>

                  {/* Contextual Inquiry Button */}
                  <a
                    href={getMailtoUrl(project)}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm text-[#011d33] bg-gradient-to-r from-cyan-300 to-blue-400 hover:from-cyan-200 hover:to-blue-300 transition-all duration-200 shadow-[0_0_20px_rgba(34,211,238,0.1)] hover:shadow-[0_0_30px_rgba(34,211,238,0.25)]"
                  >
                    <Mail className="w-4 h-4" />
                    Inquire About This System
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Dedicated Video Modal (Lazy-Loaded Player) */}
      <AnimatePresence>
        {activeVideoProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Modal Backdrop overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseVideo}
              className="absolute inset-0 bg-[#011d33]/90 backdrop-blur-md cursor-pointer"
            />

            {/* Modal Box */}
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="relative w-full max-w-4xl rounded-3xl border border-white/15 bg-gradient-to-b from-white/[0.08] to-white/[0.02] backdrop-blur-2xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden z-10 flex flex-col"
            >
              {/* Top border highlight */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-t-3xl" />

              {/* Close Button */}
              <button
                onClick={handleCloseVideo}
                className="absolute top-4 right-4 z-20 p-2.5 rounded-full bg-black/40 hover:bg-black/60 border border-white/10 hover:border-white/20 text-white/70 hover:text-white transition-all cursor-pointer"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>

              {/* HTML5 Player Section */}
              <div className="w-full aspect-video bg-black relative">
                <video
                  ref={videoRef}
                  src={activeVideoProject.videoUrl}
                  className="w-full h-full object-contain"
                  controls
                  autoPlay
                  playsInline
                  preload="auto"
                />
              </div>

              {/* Project Info Block */}
              <div className="p-6 md:p-8 border-t border-white/10 bg-black/25">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div>
                    <span className="text-[10px] font-bold tracking-widest text-cyan-400 uppercase">
                      {activeVideoProject.subtitle}
                    </span>
                    <h2 className="text-2xl font-bold font-header text-white mt-1">
                      {activeVideoProject.title}
                    </h2>
                    <p className="text-white/60 text-sm leading-relaxed mt-3 max-w-2xl">
                      {activeVideoProject.description}
                    </p>
                  </div>

                  {/* Inquiry CTA in Modal */}
                  <div className="shrink-0 flex items-center">
                    <a
                      href={getMailtoUrl(activeVideoProject)}
                      className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-sm text-[#011d33] bg-gradient-to-r from-cyan-300 to-blue-400 hover:from-cyan-200 hover:to-blue-300 transition-all duration-200 shadow-[0_0_20px_rgba(34,211,238,0.15)] hover:shadow-[0_0_35px_rgba(34,211,238,0.3)] hover:scale-[1.02]"
                    >
                      <Mail className="w-4 h-4" />
                      Inquire About Custom Setup
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PortfolioPage;
